import { captureException } from "@sentry/browser";
import type { ClientBuilderUserPermission } from "api-types/builderUsers/builderUsersResponses";
import type { BuilderAccount } from "api-types/common/responseTypes";
import { HOME_PLAN_CATEGORY_LABELS } from "api-types/homePlans/homePlanFields";
import type { ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import type { HomePlanCategory } from "api-types/homePlans/homePlanTypes";
import type { ClientLocationPlan, ClientLocationPlanListData } from "api-types/locationPlans/locationPlansResponses";
import {
  getLocationPlanName,
  isMultiUnitLocation as getIsMultiUnitLocation,
} from "api-types/locations/locationDisplay";
import type { ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { ClientUserHydrated } from "api-types/users/usersResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { isInvalidDataError } from "common/types/structuredErrorResponse";
import { assertIsNotNull, assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import type { FetchErrorResponse } from "common/utils/fetchHelpers";
import stringApplySearch from "common/utils/stringApplySearch";
import Toggle from "lib/components/actions/Toggle";
import { canCreateHomePlanOnLot } from "lib/components/builders/communities/lots/lotUtils";
import getLotNumberDisplay from "lib/components/builders/communities/plans/getLotNumberDisplay";
import { SelectPlansTable } from "lib/components/builders/communities/plans/SelectPlansTable";
import { useCreatableLocationPlans } from "lib/components/builders/communities/plans/useCreatableLocationPlans";
import SalesStaffAccessWarning from "lib/components/builders/communities/SalesStaffAccessWarning";
import { shouldDisplayPricing } from "lib/components/builders/communities/utils/pricingVisibility";
import {
  getSalesStaffResourceFilterUrl,
  grantSalesStaffResourceViewAccess,
} from "lib/components/builders/communities/utils/salesStaffResourceAccess";
import useHomePlanEvents from "lib/components/builders/communities/utils/useHomePlanEvents";
import { useLocationHierarchy } from "lib/components/builders/communities/utils/useLocationHierarchy";
import CustomerLookupInput from "lib/components/builders/CustomerLookupInput";
import { EXTERNAL_ID_OVERVIEW_MESSAGE } from "lib/constants/messages";
import { getBuilderLocationPlansURL, getBuilderLotsURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { homePlanOpsViewPath, homePlanShowroomPath, locationLotsPath } from "lib/routeHelpers";
import type { LotRowData } from "lib/state/LotsContext";
import { useUserContext } from "lib/state/UserContextProvider";
import type { PutHomePlanFields } from "lib/storage/homePlanStorage";
import { getHomePlanHydrated, HOME_PLAN_CATEGORY_DEFAULT } from "lib/storage/homePlanStorage";
import { fetchAllLotPages } from "lib/storage/lotStorage";
import getUserFullName from "lib/utils/userName";
import { useEffect, useMemo, useRef, useState } from "react";
import Link from "ui/components/Link";
import Input from "ui/forms/Input";
import InputGroupNumber from "ui/forms/InputGroupNumber";
import InputGroup from "ui/forms/InputGroupText";
import InputLabel from "ui/forms/InputLabel";
import Radio from "ui/forms/Radio";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import useAppContext from "ui/hooks/useAppContext";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import { Message } from "ui/text/Message";
import { hasMultipleHomePlanContracts } from "../homePlans/HomePlanContractList";
import { useCreateHomePlan, useUpdateHomePlan } from "../homePlans/useHomePlanAPI";
import type { BuilderModalStep } from "./BuilderModalStepped";
import BuilderModalStepped from "./BuilderModalStepped";
import styles from "./CreateOrUpdateHomePlanModal.module.scss";

interface CommunityPlanProps {
  availablePlans: ClientLocationPlan[];
  location: ClientLocationPlanListData["location"] | undefined;
  onSelect: (locationPlan: ClientLocationPlan) => void;
  selectedCommunityPlan?: ClientLocationPlan;
  builderId: string;
  disabledPlanReasons?: Record<string, string>;
}

function CommunityPlans({
  onSelect,
  availablePlans,
  location,
  selectedCommunityPlan,
  builderId,
  disabledPlanReasons,
}: CommunityPlanProps) {
  const [search, setSearch] = useState("");

  const filteredPlans = useMemo(
    () => availablePlans.filter((p) => stringApplySearch(p.name, search)),
    [availablePlans, search],
  );

  function handleSelect(id: string) {
    if (disabledPlanReasons?.[id]) {
      return;
    }
    const plan = availablePlans.find((p) => p.id === id);
    if (plan) {
      onSelect(plan);
    }
  }

  return (
    <>
      <Input
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        placeholder="Search Community Plans..."
      />
      <div style={{ overflowX: "auto" }}>
        {location && availablePlans.length ? (
          <SelectPlansTable
            availablePlans={filteredPlans}
            location={location}
            selectedIds={selectedCommunityPlan ? [selectedCommunityPlan.id] : []}
            onSelect={handleSelect}
            builderId={builderId}
            disabledPlanReasons={disabledPlanReasons}
          />
        ) : (
          "No Plans found."
        )}
      </div>
    </>
  );
}

interface EditHomePlanModalProps {
  onClose: () => void;
  builderId: string;
  locationId?: string;
  locationPlan?: ClientLocationPlan;
  homePlan?: ClientHomePlanHydratedNoDoc;
  lot?: LotRowData;
  initialLotId?: string;
  onSuccess?: (hp: ClientHomePlanHydratedNoDoc) => void;
  configurationView?: "ops" | "sales";
}

const salesStaffPermission: ClientBuilderUserPermission = "ALLOWED_AS_SALES_STAFF";

/** `Boolean(field)` is wrong for price fields (0 is valid) and lotId null. Used only for submit enablement. */
function homePlanFormHasMeaningfulUserInput(homePlanFields: object): boolean {
  const fields = homePlanFields as Record<string, unknown>;
  return Object.keys(fields).some((key) => {
    const v = fields[key];
    if (key === "lotId" && v === null) {
      return true;
    }
    if (v === 0) {
      return true;
    }
    if (typeof v === "string" && v.trim() !== "") {
      return true;
    }
    if (typeof v === "number" && !Number.isNaN(v) && v !== 0) {
      return true;
    }
    if (
      v &&
      typeof v === "object" &&
      "amount" in (v as object) &&
      typeof (v as { amount: unknown }).amount === "number"
    ) {
      return true;
    }
    if (v && typeof v === "object" && "name" in (v as object)) {
      const n = (v as { name?: string }).name;
      if (typeof n === "string" && n.trim() !== "") {
        return true;
      }
    }
    return false;
  });
}

export default function EditHomePlanModal({
  onClose,
  locationPlan,
  locationId,
  builderId,
  homePlan,
  lot,
  initialLotId,
  onSuccess,
  configurationView,
}: EditHomePlanModalProps) {
  const { setUserNotification } = useAppContext();
  const navigate = useAppNavigate();
  const sendHomePlanEvent = useHomePlanEvents(homePlan);
  const { user, isSuperAdmin, hasPermission, builder, enabledFeatures } = useUserContext();
  // Derived here, not passed in: a plan presenting several contracts has no plan-level customer,
  // category, sales staff, notes, or pricing, and the Home Plan endpoint rejects writes to them.
  // Every entry point to this modal (detail header, table row menu, phases) must hide them, and
  // deriving it from the plan is what keeps a new call site from silently omitting it.
  const hideContractFields = hasMultipleHomePlanContracts(enabledFeatures.homePlanContracts, homePlan?.contractCount);
  const createHomePlan = useCreateHomePlan(builderId);
  const updateHomePlan = useUpdateHomePlan(builderId);
  const [incompatibleOptionSetIds, setIncompatibleOptionSetIds] = useState<string[] | null>(null);
  const { data: locationHierarchy } = useLocationHierarchy(builderId, locationId ?? "");

  const { data: locationPlansData } = useFetch<ClientLocationPlanListData>(
    locationId ? getBuilderLocationPlansURL({ builderId, locationId, includeArchived: false }) : "",
    [locationId, builderId],
  );

  const { data: builderAccountData } = useFetch<BuilderAccount>(`${WEB_API_BASE_PATH}/builders/${builderId}/account`, [
    builderId,
  ]);

  const mapContainerRef = useRef(null);
  const isMultiUnitBuildingHomePlan = homePlan?.isMultiUnit === true;

  function pickLotId(lot: LotRowData | undefined, homePlan: ClientHomePlanHydratedNoDoc | undefined) {
    if (isMultiUnitBuildingHomePlan) {
      return null;
    }
    return initialLotId ?? lot?.id ?? homePlan?.lotId;
  }

  const lotId = pickLotId(lot, homePlan);

  /**
   * Set sales agent for new plan. Order of precedence (high to low):
   *
   * 1. Sales agent explicitly set in modal
   * 2. If user creating is a sales agent, use them
   * 3. Nobody
   */

  interface HomePlanFields extends PutHomePlanFields {
    updatedFromMapEvt?: boolean;
    discountAmount?: number;
    discountDescription?: string;
    totalPriceOverride?: number | null;
  }

  interface ReducedLotProps {
    label: string;
    value: string | null;
  }

  assertIsNotNullOrUndefined(user);

  const initialHomePlanFields: HomePlanFields = {
    lotId: lotId ?? null,
    externalId: homePlan?.externalId ?? null,
    planName: homePlan?.planName || homePlan?.planSetId || "",
    customerInfo: homePlan?.customerInfo
      ? {
          name: homePlan.customerInfo.name ?? undefined,
          email: homePlan.customerInfo.email ?? undefined,
          phone: homePlan.customerInfo.phone ?? undefined,
          externalId: homePlan.customerInfo.externalId ?? undefined,
        }
      : undefined,
    notes: homePlan?.notes ?? undefined,
    salesStaffId: homePlan?.salesStaff?.id ?? (!homePlan && hasPermission("ALLOWED_AS_SALES_STAFF") ? user.id : null),
    category: (homePlan?.category || HOME_PLAN_CATEGORY_DEFAULT) as HomePlanCategory,
    source: homePlan?.source ?? undefined,
    discount: homePlan?.discount,
    discountAmount: homePlan?.discount?.amount,
    discountDescription: homePlan?.discount?.description,
    totalPriceOverride: homePlan?.totalPriceOverride ?? null,
  };

  const [homePlanFields, setHomePlanFields] = useState(initialHomePlanFields);
  const [showTotalPriceOverride, setShowTotalPriceOverride] = useState(!!initialHomePlanFields.totalPriceOverride);
  const routeToShowroom = configurationView !== "ops";

  const isMultiUnitLocation = getIsMultiUnitLocation(locationPlansData?.location);

  const noLotOption: OptionProps<string | null> = { label: "No lot", value: null };
  const currentLotOption: OptionProps = {
    label: getLotNumberDisplay(lot ?? homePlan?.lot),
    value: lotId ?? null,
  };

  // Seed options shown before fetchLotsForHomePlan completes. Multi-unit creates require a
  // lot (enforced by `requiresLot` below); edits may unassign.
  function buildInitialLotOptions(): OptionProps[] {
    if (isMultiUnitBuildingHomePlan) {
      return [noLotOption];
    }
    if (isMultiUnitLocation && !homePlan) {
      return lotId ? [currentLotOption] : [];
    }
    return lotId ? [noLotOption, currentLotOption] : [noLotOption];
  }

  const [availableLotOptions, setAvailableLotOptions] = useState<OptionProps[]>(buildInitialLotOptions);
  const [selectedCommunityPlan, setSelectedCommunityPlan] = useState<ClientLocationPlan | null>(null);
  const targetLocationPlanId = homePlan?.communityPlanId ?? locationPlan?.id ?? selectedCommunityPlan?.id ?? null;
  const skipLotFetch = !!lot || isMultiUnitBuildingHomePlan; // skip when lot is provided, or is MU building home plan
  const [lotFetchSettled, setLotFetchSettled] = useState(skipLotFetch);
  // True until a community plan is known and its lot fetch has finished (skipped when lot prop / multi-unit shell).
  const lotsLoading = !skipLotFetch && (!targetLocationPlanId || !lotFetchSettled);

  const { data: salesStaff } = useFetch<ClientUserHydrated[]>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/users-internal?permission=${salesStaffPermission}`,
    [builderId],
  );

  const locationPlanAccessUrl = getSalesStaffResourceFilterUrl({
    builderId,
    resourceType: "community_plans",
    resourceId: targetLocationPlanId,
  });
  const { data: usersWithLocationPlanAccess, mutate: mutateUsersWithLocationPlanAccess } = useFetch<
    ClientUserHydrated[]
  >(locationPlanAccessUrl ?? "", [builderId, targetLocationPlanId], { handleErrorLocally: true });
  const usersWithLocationPlanAccessSet = new Set((usersWithLocationPlanAccess ?? []).map((u) => u.id));
  const eligibleSalesStaff = targetLocationPlanId
    ? (salesStaff ?? []).filter((sa) => usersWithLocationPlanAccessSet.has(sa.id))
    : salesStaff ?? [];
  const staffOpts = eligibleSalesStaff.length
    ? eligibleSalesStaff.map((sa) => ({
        label: getUserFullName(sa),
        value: sa.id,
      }))
    : [];

  if (isSuperAdmin || user.tempUserRole === "sales_agent") {
    staffOpts.push({
      label: getUserFullName(user),
      value: user.id,
    });
  }

  const salesStaffOptions: OptionProps[] = [{ label: "None", value: "" }, ...staffOpts];
  const selectedSalesStaff = (salesStaff ?? []).find((sa) => sa.id === homePlanFields.salesStaffId);
  const staleSalesStaffAssignment =
    !!targetLocationPlanId && !!selectedSalesStaff && !usersWithLocationPlanAccessSet.has(selectedSalesStaff.id);

  const handleGrantViewAccessForSalesStaff = async () => {
    if (!selectedSalesStaff || !targetLocationPlanId) {
      return;
    }

    try {
      await grantSalesStaffResourceViewAccess({
        builderId,
        targetUser: selectedSalesStaff,
        resourceType: "community_plans",
        resourceId: targetLocationPlanId,
      });
      await mutateUsersWithLocationPlanAccess();
      setUserNotification({
        message: `Granted location plan view access to ${getUserFullName(selectedSalesStaff)}.`,
        messageType: "success",
      });
    } catch {
      setUserNotification({
        message: "Could not grant location plan access to selected sales staff.",
        messageType: "error",
      });
    }
  };
  const categoryOptions: OptionProps<string>[] = Object.entries(HOME_PLAN_CATEGORY_LABELS).map(([value, label]) => ({
    label,
    value,
  }));

  const fetchLotsForHomePlan = async (): Promise<void> => {
    if (skipLotFetch) {
      if (isMultiUnitBuildingHomePlan) {
        setAvailableLotOptions([noLotOption]);
      }
      setLotFetchSettled(true);
      return;
    }

    if (!locationId || !targetLocationPlanId) {
      setLotFetchSettled(false);
      return;
    }

    setLotFetchSettled(false);
    try {
      const lots = await fetchAllLotPages<ClientLotHydrated>((pagination) =>
        getBuilderLotsURL({
          builderId,
          locationId,
          locationPlanId: targetLocationPlanId,
          pagination,
        }),
      );

      // Omit "No lot" only when creating in multi-unit; edits may unassign.
      const seed: ReducedLotProps[] = isMultiUnitLocation && !homePlan ? [] : [noLotOption];

      const filteredLots = lots.filter((lot: ClientLotHydrated) => {
        // Always allow the current home plan's lot (for editing existing plans)
        if (lot.id === homePlan?.lotId) {
          return true;
        }
        return canCreateHomePlanOnLot(lot);
      });

      const reducedLots = filteredLots
        .reduce((acc: ReducedLotProps[], curr: ClientLotHydrated) => {
          if (!acc.find((item) => item.value === curr.id)) {
            acc.push({ label: getLotNumberDisplay(curr), value: curr.id });
          }
          return acc;
        }, seed)
        .sort((a: ReducedLotProps, b: ReducedLotProps) => parseInt(a.label) - parseInt(b.label));

      setAvailableLotOptions(reducedLots);
    } catch (error) {
      captureException(error, {
        extra: { builderId, locationId, locationPlanId: targetLocationPlanId },
      });
      console.error(
        "Failed to fetch lots for home plan modal",
        "builderId:",
        builderId,
        "locationId:",
        locationId,
        "locationPlanId:",
        targetLocationPlanId,
        "error:",
        error,
      );
    } finally {
      setLotFetchSettled(true);
    }
  };

  useEffect(() => {
    void fetchLotsForHomePlan();
  }, [locationId, targetLocationPlanId, homePlan?.lotId, lot, skipLotFetch]);

  // When lot is provided, fetchLotsForHomePlan is skipped, but we still need to correct the initial
  // lot options once isMultiUnitLocation becomes available (locationPlansData loads asynchronously)
  useEffect(() => {
    if (lot && locationPlansData) {
      setAvailableLotOptions(buildInitialLotOptions());
    }
  }, [lot, locationPlansData]);

  // reset price on modal close / open
  useEffect(() => {
    setHomePlanFields(initialHomePlanFields);
    setSelectedCommunityPlan(null);
  }, []);

  const displayPrices = shouldDisplayPricing(
    "internalHomePlanLinks",
    locationPlansData?.location,
    locationHierarchy?.ancestors,
    builderAccountData?.builder,
  );
  const canSetHomePlanCategory = hasPermission("SET_CATEGORY_ON_HOME_PLANS");

  const handleSubmit = async (setIsSaving?: (arg0: boolean) => void) => {
    const { discountAmount, discountDescription, ...payloadFields } = homePlanFields;

    if (isMultiUnitBuildingHomePlan) {
      delete payloadFields.lotId;
    }

    if (isMultiUnitBuildingHomePlan || hideContractFields) {
      delete payloadFields.customerInfo;
      delete payloadFields.category;
      delete payloadFields.salesStaffId;
    }

    if (hideContractFields) {
      delete payloadFields.notes;
      delete payloadFields.totalPriceOverride;
      delete payloadFields.discount;
    }

    if (!hideContractFields && discountAmount !== undefined && !isNaN(discountAmount)) {
      (payloadFields as any).discount = { amount: discountAmount, description: discountDescription };
    }

    if (homePlan) {
      try {
        await updateHomePlan.mutateAsync({ homePlanId: homePlan.id, fields: payloadFields });
      } catch (error) {
        const fetchError = error as FetchErrorResponse;
        if (isInvalidDataError(fetchError.data)) {
          setIncompatibleOptionSetIds(fetchError.data.fields);
        }
        setUserNotification(fetchError);
        if (setIsSaving) {
          setIsSaving(false);
        }
        return;
      }

      if (onSuccess) {
        const [homePlanHydrated, err] = await getHomePlanHydrated(builderId, homePlan.id);
        if (err) {
          setUserNotification(err);
          if (setIsSaving) {
            setIsSaving(false);
          }
          return;
        }
        onSuccess(ensureDefined(homePlanHydrated));
        sendHomePlanEvent("config:updated_homeplan");
      }

      setUserNotification({
        message: "Home plan updated",
        messageType: "success",
      });

      if (setIsSaving) {
        setIsSaving(false);
      }
      onClose();
    } else {
      payloadFields.source = "sales_agent";
      const cp = locationPlan ?? selectedCommunityPlan;
      if (!cp) {
        return; // submittable gate prevents this, but guard for safety
      }
      if (user.tempUserRole === "sales_agent") {
        payloadFields.salesStaffId = user.id;
      }

      let res;
      try {
        res = await createHomePlan.mutateAsync({ ...payloadFields, communityPlanId: cp.id });
      } catch (error) {
        const fetchError = error as FetchErrorResponse;
        if (isInvalidDataError(fetchError.data)) {
          setIncompatibleOptionSetIds(fetchError.data.fields);
        }
        setUserNotification(fetchError);
        if (setIsSaving) {
          setIsSaving(false);
        }
        return;
      }

      assertIsNotNull(res.id);
      sendHomePlanEvent("config:created_homeplan", {
        homePlanId: res.id,
        homePlanName: res.planName,
        homePlanStatus: res.planStatus,
        homePlanCategory: res.category,
        homePlanSalesStaffId: res.salesStaffId,
        communityPlanId: cp.id,
        lotId: res.lotId,
      });

      if (routeToShowroom) {
        void navigate({ to: homePlanShowroomPath(builderId, res.id) });
      } else {
        void navigate({ to: homePlanOpsViewPath(builderId, res.id) });
      }
    }
  };

  const steps: BuilderModalStep[] = [];

  const availableLotCount = availableLotOptions?.filter((opt) => opt.value)?.length ?? 0;
  const canEditHomePlanLot = hasPermission("EDIT_HOME_PLAN_LOT");
  const lotIsLocked = (homePlan?.planStatus && homePlan?.planStatus !== "PREP") || !!homePlan?.lockedAt;
  const masterPlanName = homePlan?.masterPlanName ?? "";
  const requiresLot = isMultiUnitLocation && !homePlan;
  const modalHeader = isMultiUnitBuildingHomePlan
    ? "Edit Multi-Unit Home Plan"
    : `${homePlan ? "Edit" : "New"} Home Plan`;

  const { availableLocationPlans, creatableLocationPlans, disabledPlanReasons, preselectedLocationPlanBlockMessage } =
    useCreatableLocationPlans({ locationPlansData, lot, homePlan, locationPlan });

  // Show the plan picker in create mode, but not when we can act without it:
  // - zero plans: skip the picker; a "no plans" warning is shown instead.
  // - exactly one plan that is creatable: skip the picker and auto-select it below.
  // Otherwise show it. `available > 1` covers multiple plans;
  // `creatable !== 1` keeps the picker for a lone *blocked* plan so tooltip can explain why you can't create homeplan.
  const showPlanSelectionStep =
    !homePlan &&
    !locationPlan &&
    availableLocationPlans.length > 0 &&
    (availableLocationPlans.length > 1 || creatableLocationPlans.length !== 1);

  // Auto-select the single creatable plan only when the picker is skipped, so we never
  // silently pre-select a plan while the selection table is visible.
  useEffect(() => {
    if (
      !homePlan &&
      !locationPlan &&
      !showPlanSelectionStep &&
      creatableLocationPlans.length === 1 &&
      !selectedCommunityPlan
    ) {
      setSelectedCommunityPlan(creatableLocationPlans[0]);
    }
  }, [homePlan, locationPlan, showPlanSelectionStep, creatableLocationPlans, selectedCommunityPlan]);

  // Reason the currently selected plan is blocked (or undefined if not blocked)
  const planBlockedReason = selectedCommunityPlan ? disabledPlanReasons[selectedCommunityPlan.id] : undefined;

  if (showPlanSelectionStep) {
    steps.push({
      header: "New Home Plan",
      subheader: locationPlansData?.location
        ? `Choose a ${getLocationPlanName(
            locationPlansData?.location,
            builder?.locationSettings,
          )} to base the Home Plan on.`
        : "",
      body: (
        <CommunityPlans
          availablePlans={availableLocationPlans}
          location={locationPlansData?.location}
          onSelect={(pv) => setSelectedCommunityPlan(pv)}
          selectedCommunityPlan={selectedCommunityPlan ?? undefined}
          builderId={builderId}
          disabledPlanReasons={disabledPlanReasons}
        />
      ),
      submittable: selectedCommunityPlan !== null && !planBlockedReason,
      notSubmittableTooltipText: planBlockedReason,
      modalProps: { size: "medium" },
    });
  }

  const subheader = homePlan?.lockedAt ? "Home Plan is locked. Unlock the Home Plan to edit information below." : "";

  const noPlanAvailable = !homePlan && !locationPlan && !selectedCommunityPlan;
  const createBlockedMessage = preselectedLocationPlanBlockMessage ?? planBlockedReason;

  steps.push({
    header: modalHeader,
    subheader,
    body: (
      <>
        {createBlockedMessage && (
          <div style={{ marginBottom: 24 }}>
            <Message variant="inverted" messageType="warning">
              <span>{createBlockedMessage}</span>
            </Message>
          </div>
        )}
        {noPlanAvailable && !createBlockedMessage && (
          <div style={{ marginBottom: 24 }}>
            <Message variant="inverted" messageType="warning">
              <span>No plans are available at this location. A plan must be added before creating a Home Plan.</span>
            </Message>
          </div>
        )}
        {!isMultiUnitBuildingHomePlan && !homePlan?.lockedAt && !lotsLoading && availableLotCount === 0 && (
          <div style={{ marginBottom: 24 }}>
            <Message variant="inverted" messageType="warning">
              <span>
                There are no available lots for this {masterPlanName} plan.{" "}
                {hasPermission("EDIT_LOTS") && locationId && (
                  <>
                    To assign a lot, make sure there is at least one Lot in the{" "}
                    <Link // strict null TODO: https://linear.app/higharc/issue/CFG-603/home-plans-page-produces-undefined-community-id-url-when-editing-a
                      href={locationLotsPath(builderId, locationId)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Lot List
                    </Link>{" "}
                    with an &ldquo;Available&rdquo; status and at least one community plan assigned.
                  </>
                )}
              </span>
            </Message>
          </div>
        )}
        {isMultiUnitBuildingHomePlan && (
          <Message messageType="info" className={styles.multiUnitInfoMessage}>
            This home plan represents the full building. Lot, customer, and category are managed on the individual unit
            home plans.
          </Message>
        )}
        <div ref={mapContainerRef} className="modal-body-wrapper">
          <section className="modal-body-form-section">
            {!isMultiUnitBuildingHomePlan &&
              (lotIsLocked ? (
                <InputGroup
                  type="text"
                  labelText="Lot Number"
                  inputValue={homePlan?.lot?.lotNumber ? getLotNumberDisplay(homePlan.lot) : noLotOption.label}
                  disabled
                  title={
                    homePlan?.planStatus !== "PREP"
                      ? "Lot cannot be edited after Home Plan is moved to Pre-Construction."
                      : undefined
                  }
                />
              ) : (
                <>
                  <InputLabel text="Lot Number" />
                  <Select
                    placeholder={lotsLoading ? "Loading lots..." : "No lot"}
                    value={homePlanFields.lotId}
                    // Empty while loading so the null "No lot" seed option doesn't override the placeholder
                    options={lotsLoading ? [] : availableLotOptions}
                    disabled={!canEditHomePlanLot || !!homePlan?.lockedAt || !locationId || lotsLoading}
                    onChange={(value) => {
                      setIncompatibleOptionSetIds(null);
                      return setHomePlanFields({
                        ...homePlanFields,
                        lotId: value as string | null | undefined,
                        updatedFromMapEvt: false,
                      });
                    }}
                    showScrollbar
                  />
                  {incompatibleOptionSetIds && incompatibleOptionSetIds.length > 0 && (
                    <div className={styles.incompatibleOptionSetContainer}>
                      <Message variant="inverted" messageType="error">
                        The following Option Set(s) are incompatible with the selected lot:
                        <div className={styles.optionSetList}>
                          {incompatibleOptionSetIds.map((optionSetId) => (
                            <li key={optionSetId}>{optionSetId}</li>
                          ))}
                        </div>
                      </Message>
                    </div>
                  )}
                </>
              ))}

            <div
              style={{
                marginTop: isMultiUnitBuildingHomePlan ? 0 : 24,
                display: "inline-flex",
                justifyContent: "space-between",
                gap: "14px",
                width: "100%",
              }}
            >
              <div style={{ width: "100%" }}>
                <InputGroup
                  type="text"
                  labelText="Plan ID"
                  placeholder="Enter Plan ID"
                  inputValue={homePlanFields.planName}
                  disabled={!!homePlan?.lockedAt}
                  onChange={(value: string) => setHomePlanFields({ ...homePlanFields, planName: value })}
                  maxLength={28}
                />
              </div>
              <div style={{ width: "100%" }}>
                <InputGroup
                  type="text"
                  tooltip={EXTERNAL_ID_OVERVIEW_MESSAGE}
                  labelText="External ID"
                  placeholder="Enter External ID"
                  inputValue={homePlanFields.externalId}
                  disabled={!!homePlan?.lockedAt}
                  onChange={(value: string) => setHomePlanFields({ ...homePlanFields, externalId: value })}
                />
              </div>
            </div>
            {!isMultiUnitBuildingHomePlan && !hideContractFields && (
              <>
                <div style={{ marginTop: 24 }} />
                <InputLabel text="Customer Category" required />
                <div className={styles.categoryRadioGroup}>
                  {categoryOptions.map((category) => (
                    <div key={category.value} className={styles.categoryRadioGroupOption}>
                      <Radio
                        name="category"
                        value={category.value}
                        onChange={(e) =>
                          setHomePlanFields({ ...homePlanFields, category: e.target.value as HomePlanCategory })
                        }
                        checked={homePlanFields.category === category.value}
                        label={category.label}
                        disabled={
                          !canSetHomePlanCategory ||
                          !!homePlan?.lockedAt ||
                          (homePlan && homePlan.planStatus !== "PREP" && !!homePlan.category)
                        }
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
            {!hideContractFields && salesStaff?.length ? (
              <>
                <div style={{ marginTop: 24 }} />
                <InputLabel text="Sales Agent" />
                <Select
                  value={homePlanFields.salesStaffId ?? locationPlansData?.location?.salesStaffId}
                  onChange={(salesStaffId) => {
                    const staffId = typeof salesStaffId === "string" && salesStaffId !== "" ? salesStaffId : null;
                    return setHomePlanFields({ ...homePlanFields, salesStaffId: staffId });
                  }}
                  placeholder="None"
                  options={salesStaffOptions}
                  disabled={!!homePlan?.lockedAt || !hasPermission("ASSIGN_SALES_AGENT_TO_HOME_PLAN")}
                  title="Only admins and managers are allowed to assign a Sales Agent to a Home Plan."
                />
                {staleSalesStaffAssignment && selectedSalesStaff ? (
                  <SalesStaffAccessWarning
                    assigneeName={getUserFullName(selectedSalesStaff)}
                    resourceLabel="location plan"
                    canGrantAccess={hasPermission("EDIT_STAFF_PERMISSIONS")}
                    onGrantAccess={handleGrantViewAccessForSalesStaff}
                    onRemoveAssignment={() => setHomePlanFields({ ...homePlanFields, salesStaffId: null })}
                  />
                ) : null}
              </>
            ) : null}
            {!isMultiUnitBuildingHomePlan && !hideContractFields && (
              <div style={{ marginTop: 24 }}>
                <CustomerLookupInput
                  value={homePlanFields.customerInfo?.name}
                  disabled={!!homePlan?.lockedAt}
                  onChange={(customerInfo: Partial<HomePlanFields["customerInfo"]>) => {
                    setHomePlanFields({
                      ...homePlanFields,
                      customerInfo: { ...homePlanFields.customerInfo, ...customerInfo },
                    });
                  }}
                />
              </div>
            )}
            {displayPrices && !homePlan?.isMultiUnit && !hideContractFields && (
              <>
                <div className={styles.totalPriceOverrideToggle}>
                  <label>Use Total Price Override</label>
                  <Toggle
                    isTrue={showTotalPriceOverride}
                    onChange={(isTrue) => {
                      setShowTotalPriceOverride(isTrue);
                      if (isTrue) {
                        // Clear price adjustments when enabling total price override
                        setHomePlanFields({
                          ...homePlanFields,
                          discountAmount: 0,
                          discountDescription: "",
                        });
                      } else {
                        // Clear total price override when disabling
                        setHomePlanFields({
                          ...homePlanFields,
                          totalPriceOverride: null,
                        });
                      }
                    }}
                    disabled={
                      !!homePlan?.lockedAt ||
                      (!hasPermission("EDIT_HOME_PLAN_DISCOUNT") &&
                        !hasPermission("EDIT_HOME_PLAN_TOTAL_PRICE_OVERRIDE"))
                    }
                    variants="small"
                  />
                </div>
                {!showTotalPriceOverride ? (
                  <>
                    <div style={{ marginTop: 24 }}>
                      <InputGroupNumber
                        labelText="Price Adjustment Amount"
                        placeholder="0"
                        inputValue={homePlanFields.discountAmount ?? 0}
                        disabled={!!homePlan?.lockedAt || !hasPermission("EDIT_HOME_PLAN_DISCOUNT")}
                        onChange={(value: number | null) => {
                          setHomePlanFields({ ...homePlanFields, discountAmount: value ?? 0 });
                        }}
                      />
                    </div>
                    <div style={{ marginTop: 24 }}>
                      <InputGroup
                        type="text"
                        labelText="Price Adjustment Description"
                        placeholder="Enter a description for the adjustment"
                        inputValue={homePlanFields.discountDescription}
                        disabled={!!homePlan?.lockedAt || !hasPermission("EDIT_HOME_PLAN_DISCOUNT")}
                        onChange={(value: string) =>
                          setHomePlanFields({ ...homePlanFields, discountDescription: value })
                        }
                      />
                    </div>
                  </>
                ) : (
                  <div style={{ marginTop: 24 }}>
                    <InputGroupNumber
                      labelText="Total Price Override"
                      placeholder="Enter total price override"
                      disabled={!!homePlan?.lockedAt || !hasPermission("EDIT_HOME_PLAN_TOTAL_PRICE_OVERRIDE")}
                      onChange={(value: number | null) => {
                        setHomePlanFields({ ...homePlanFields, totalPriceOverride: value ?? null });
                      }}
                      tooltip="This will be the fixed price of the Home Plan."
                      {...(homePlanFields.totalPriceOverride ? { inputValue: homePlanFields.totalPriceOverride } : {})}
                    />
                  </div>
                )}
              </>
            )}
            {!hideContractFields && (
              <div style={{ marginTop: 24 }}>
                <InputGroup
                  type="textarea"
                  labelText="Notes"
                  placeholder="Enter notes about the Home Plan"
                  inputValue={homePlanFields.notes}
                  disabled={!!homePlan?.lockedAt}
                  onChange={(value: string) => setHomePlanFields({ ...homePlanFields, notes: value })}
                />
              </div>
            )}
          </section>
        </div>
      </>
    ),
    indicateRequiredFields: !isMultiUnitBuildingHomePlan,
    modalProps: { scrollModal: true },
    submittable:
      !homePlan?.lockedAt &&
      !createBlockedMessage &&
      !!(homePlan || locationPlan || selectedCommunityPlan) &&
      homePlanFormHasMeaningfulUserInput(homePlanFields) &&
      homePlanFields.category !== null &&
      (!requiresLot || !!homePlanFields.lotId),
    notSubmittableTooltipText: (() => {
      if (createBlockedMessage) {
        return createBlockedMessage;
      }
      if (requiresLot && !homePlanFields.lotId) {
        return "A lot selection is required for multi-unit locations.";
      }
      return undefined;
    })(),
  });

  return (
    <BuilderModalStepped
      modalProps={{
        open: true,
        onClose,
        size: "small",
        className: `${styles.homePlanModal} ${isMultiUnitBuildingHomePlan ? styles.multiUnitHomePlanModal : ""}`,
      }}
      steps={steps}
      onSubmit={handleSubmit}
    />
  );
}
